<script setup lang="ts">
import type { LoginAwaitSelectorItem } from '@/api/login'
import { LoginMobileTicketConfirm, LoginPwdTicketConfirm } from '@/api/login'
import { logon } from '@mp/api/gateway/UserControler.js'
import { IconFont } from '@mp/component'
import { useMutation } from '@tanstack/vue-query'
import { ref } from 'vue'
import Sms from './DoubleVerification/Sms.vue'
import Password from './DoubleVerification/Password.vue'
import { getDeviceCode } from '@mp/utils'
import { ZF_NO } from '@mp/constant'

const props = defineProps<{
  type: 'account' | 'sms'
}>()

const show = ref<boolean>(false)
const list = ref<LoginAwaitSelectorItem[]>()
const checked = ref<LoginAwaitSelectorItem>()
const loginTicket = ref<string>()

const smsRef = ref<InstanceType<typeof Sms>>()
const passwordRef = ref<InstanceType<typeof Password>>()

// #登录
const { mutateAsync: mutateLogonAsync } = useMutation({
  mutationFn: logon,
})

const { mutateAsync, isLoading, data: loginConfirmData } = useMutation({
  mutationFn: props.type === 'account' ? LoginPwdTicketConfirm : LoginMobileTicketConfirm,
  async onSuccess(data) {
    // sessionStorage.setItem('accessToken', data?.accessToken || '')
    const deviceId = await getDeviceCode()

    if (data?.needTwoFactorAuth)
      return data.twoFactorAuthType === 'SMS_VERIFY' ? smsRef.value?.open() : passwordRef.value?.open()

    return mutateLogonAsync({
      appId: ZF_NO,
      deviceId,
      accessToken: data?.accessToken,
    }).then(() => {
      window.location.replace(`${window.location.origin}/merchant-platform/#/dashboard`)
      return true
    }).catch(() => undefined)
  },
})

async function handleChecked(record: LoginAwaitSelectorItem, loginTicket: string) {
  checked.value = record
  if (!loginTicket)
    return

  return await mutateAsync({
    loginTicket,
    operatorId: record.operatorId,
  }).catch(() => undefined)
}

defineExpose({
  open(params: {
    data: LoginAwaitSelectorItem[]
    loginTicket: string
  }) {
    loginTicket.value = params.loginTicket
    if (params.data)
      list.value = params.data

    show.value = true
  },
  handleChecked,
})
</script>

<template>
  <el-dialog v-model="show" :width="520" append-to-body style="--el-dialog-padding-primary: 30px;">
    <!-- header  -->
    <template #header>
      <h3 class=" text-lg mb-2">
        选择账号登录
      </h3>
      <p class="text-gary1 text-sm">
        您的手机号关联多个账号，请选择您需要登录的账号
      </p>
    </template>

    <!-- conetnt  -->
    <section class=" space-y-2">
      <div
        v-for="item in list" :key="item.operatorId" v-loading="isLoading"
        class=" relative flex p-4 border border-gary2 border-solid cursor-pointer rounded-2 transition-all hover:border-primary hover:shadow-xl"
        :class="{
          'bg-[#ECF6FD] border-primary': checked?.operatorId === item.operatorId,
        }" @click="handleChecked(item, loginTicket!)"
      >
        <div class="flex">
          <img src="@/assets/avatar1.svg" class="w-[48px] h-[48px] bg-gary1 rounded-full mr-4 block" alt="">
          <div class="flex-1">
            <div class="flex">
              <label class=" block text-md text-black">{{ item.displayPrincipalName }}</label>
              <el-tag v-if="item.whetherDefaultOperator" class="ml-2" type="warning" size="small" effect="dark">
                管理员
              </el-tag>
              <el-tag v-else class="ml-2" type="warning" size="small">
                操作员
              </el-tag>
            </div>
            <p v-if="item.ext?.belongMchId" class=" text-gary1 text-sm mt-2 mb-0">
              商户号： {{ item.ext.belongMchId }}
            </p>
          </div>
        </div>

        <IconFont
          v-if="checked?.operatorId === item.operatorId" type="icon-check"
          class=" text-primary text-2xl absolute right-4 top-1/2 -translate-y-1/2"
        />
      </div>
    </section>
  </el-dialog>

  <Sms ref="smsRef" :data="loginConfirmData!" />
  <Password ref="passwordRef" :data="loginConfirmData!" />
</template>
